import { arrayMove } from "@dnd-kit/sortable";
import { makeAutoObservable, runInAction } from "mobx";

export type MediaType = "image" | "video" | "vector";

export type ModeMediaType = "logotype" | "media";

export type MediaFile = {
  id?: string;
  file?: File;
  type: string;
  previewUrl: string;
};

export type MediaMetadata = {
  type: MediaType;
  mimeType: string;
  size: number;
  width?: number;
  height?: number;
  name: string;
};

export type DimensionConstraints = {
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
};

export type MediaTypeValidation = {
  acceptedMimeTypes: string[];
  maxSize: number;
  minSize?: number;
  dimensionConstraints?: DimensionConstraints;
};

export type ValidationConfig = {
  image?: MediaTypeValidation;
  video?: MediaTypeValidation;
  vector?: MediaTypeValidation;
  limits: {
    maxImages?: number;
    maxVideos?: number;
    maxLogotypes?: number;
    maxTotal?: number;
  };
  logotypeDimensionConstraints?: DimensionConstraints;
};

const DEFAULT_VALIDATION_CONFIG: ValidationConfig = {
  limits: {},
};

export class MediaStore {
  mediaFiles: MediaFile[] = [];
  removedIds: string[] = [];
  private validationConfig: ValidationConfig;

  constructor(
    init?: MediaFile[],
    validationConfig: ValidationConfig = DEFAULT_VALIDATION_CONFIG
  ) {
    makeAutoObservable(this);
    this.validationConfig = validationConfig;
    this.restoreFromPayload(init);
  }

  restoreFromPayload(payload?: MediaFile[]) {
    runInAction(() => {
      this.mediaFiles = payload ?? [];
    });
  }

  get isImageLimitReached() {
    const maxImages = this.validationConfig.limits.maxImages ?? 0;
    return this.images.length >= maxImages;
  }

  get isVideoLimitReached() {
    const maxVideos = this.validationConfig.limits.maxVideos ?? 0;
    return this.videos.length >= maxVideos;
  }

  clearMedia() {
    this.mediaFiles = [];
  }

  get images() {
    return this.mediaFiles.filter(
      f =>
        this.getMediaCategory(f.type) === "image" ||
        this.getMediaCategory(f.type) === "vector"
    );
  }

  get videos() {
    return this.mediaFiles.filter(
      f => this.getMediaCategory(f.type) === "video"
    );
  }

  addMediaFile(file: File) {
    const media: MediaFile = {
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
      type: file.type,
    };
    runInAction(() => {
      this.mediaFiles.push(media);
    });
  }

  replaceFileAt(index: number, file: File) {
    const existing = this.mediaFiles[index];
    if (existing?.previewUrl && existing.previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(existing.previewUrl);
    }

    const media: MediaFile = {
      file,
      previewUrl: URL.createObjectURL(file),
      type: file.type,
    };

    runInAction(() => {
      this.mediaFiles[index] = media;
    });
  }

  removeMediaFile(index: number) {
    const removed = this.mediaFiles[index];
    if (!removed) return;
    if (removed.id) {
      this.removedIds.push(removed.id);
    }
    if (removed.previewUrl && removed.previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(removed.previewUrl);
    }
    this.mediaFiles.splice(index, 1);
  }

  resetStore() {
    this.mediaFiles.forEach(f => {
      if (f.previewUrl && f.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(f.previewUrl);
      }
    });

    this.mediaFiles = [];
  }

  get isTotalLimitReached() {
    const maxTotal = this.validationConfig.limits.maxTotal ?? Infinity;
    return this.mediaFiles.length >= maxTotal;
  }

  canAddMedia(metadata: MediaMetadata): void {
    const { maxImages, maxVideos, maxTotal } = this.validationConfig.limits;

    if (maxTotal && this.mediaFiles.length >= maxTotal) {
      throw new Error(`Limit exceeded: maximum ${maxTotal} media files.`);
    }

    // Determine media category (image, vector, or video)
    const mediaCategory = this.getMediaCategory(metadata.mimeType);
    if (!mediaCategory) {
      throw new Error("Unsupported file format.");
    }

    const validation = this.validationConfig[mediaCategory];
    if (!validation) {
      throw new Error("Unsupported file format.");
    }

    if (!maxTotal) {
      if (
        (mediaCategory === "image" || mediaCategory === "vector") &&
        maxImages !== undefined &&
        this.images.length >= maxImages
      ) {
        throw new Error(`Limit exceeded: maximum ${maxImages} images.`);
      }

      if (
        mediaCategory === "video" &&
        maxVideos !== undefined &&
        this.videos.length >= maxVideos
      ) {
        throw new Error(`Limit exceeded: maximum ${maxVideos} videos.`);
      }
    }

    // Check MIME type
    if (!validation.acceptedMimeTypes.includes(metadata.mimeType)) {
      throw new Error("Unsupported file format.");
    }

    // Check file size
    if (metadata.size > validation.maxSize) {
      const maxSizeMB = validation.maxSize / (1024 * 1024);
      throw new Error(
        `${mediaCategory === "video" ? "Video" : "Image"} must be less than ${maxSizeMB} MB.`
      );
    }

    if (validation.minSize && metadata.size < validation.minSize) {
      throw new Error("File is too small.");
    }

    // Check dimension constraints
    const dimensionConstraints = validation.dimensionConstraints;

    if (dimensionConstraints && metadata.width && metadata.height) {
      if (
        dimensionConstraints.minWidth &&
        metadata.width < dimensionConstraints.minWidth
      ) {
        throw new Error(
          `The file "${metadata.name}" does not meet the resolution requirements. Minimum width: ${dimensionConstraints.minWidth}px.`
        );
      }

      if (
        dimensionConstraints.minHeight &&
        metadata.height < dimensionConstraints.minHeight
      ) {
        throw new Error(
          `The file "${metadata.name}" does not meet the resolution requirements. Minimum height: ${dimensionConstraints.minHeight}px.`
        );
      }

      if (
        dimensionConstraints.maxWidth &&
        metadata.width > dimensionConstraints.maxWidth
      ) {
        throw new Error(
          `The file "${metadata.name}" exceeds maximum width: ${dimensionConstraints.maxWidth}px.`
        );
      }

      if (
        dimensionConstraints.maxHeight &&
        metadata.height > dimensionConstraints.maxHeight
      ) {
        throw new Error(
          `The file "${metadata.name}" exceeds maximum height: ${dimensionConstraints.maxHeight}px.`
        );
      }
    }
  }

  getMediaCategory(mimeType: string): "image" | "video" | "vector" | null {
    if (mimeType === "image/svg+xml") {
      return "vector";
    }
    if (mimeType.startsWith("image/")) {
      return "image";
    }
    if (mimeType.startsWith("video/")) {
      return "video";
    }
    return null;
  }

  moveMedia(oldIndex: number, newIndex: number) {
    runInAction(() => {
      this.mediaFiles = arrayMove(this.mediaFiles, oldIndex, newIndex);
    });
  }
}
